import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { View, Flex, Text, Button } from "./Common/spectrumCompat";
import { displayToasts } from "../Helpers/displayAppToast";
import {
  getDxpAddAdobeIdMessage,
  getDXPRegistrationLink,
} from "../Helpers/utils";
import { setRegisteredCompanies } from "../Redux/actions";
import DxpRegistrationTopHeader from "./DxpRegistrationTopHeader";
import WizardNavLayout from "./WizardNavLayout";
import "./css/registration.css";
import adobeIdConfig from "../ContentConfig/AdobeIdStep";
import useIMSReady from "../Hooks/useIMSReady";
import { prepareDxpImsRedirectStorage } from "../Helpers/dxpAdobeImsAuth";
import { ensureDxpAdobeImsBootstrapped } from "../Helpers/dxpImsBootstrap";

const PROGRAM = "DXP";

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-enable @typescript-eslint/no-explicit-any */

function getStepLabel() {
  const selectedType = localStorage.getItem("selectedRegisterCompanyType");
  let total = 3;
  if (selectedType === "subsidaryCompany") {
    total = 4;
  } else if (selectedType === "standaloneCompany") {
    total = 5;
  }
  return `STEP 2/${total}`;
}

export type AdobeIdMessageHtmlProps = {
  html: string;
  /** Extra classes (default typography uses `.dxp-adobe-id-message-html`). */
  className?: string;
};

export function AdobeIdMessageHtml({
  html,
  className,
}: AdobeIdMessageHtmlProps) {
  return (
    <div
      className={["dxp-adobe-id-message-html", className]
        .filter(Boolean)
        .join(" ")}
      // eslint-disable-next-line react/no-danger -- CMS-style HTML message
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

type AdobeIdVerificationProps = {
  previewMode?: boolean;
};

function AdobeIdVerification({
  previewMode = false,
}: AdobeIdVerificationProps) {
  const [isIMSReady] = useIMSReady();
  const dispatch = useDispatch();
  const registration = useSelector(
    (state: { registration?: Record<string, unknown> }) =>
      state?.registration || ({} as Record<string, unknown>),
  );
  const registeredCompanies =
    (registration?.registeredCompanies as unknown[]) || [];
  const imsProfileExists = !!registration?.imsProfileExists;
  const email = (localStorage.getItem("email") as string) || "";

  const message = getDxpAddAdobeIdMessage({
    email,
    imsProfileExists,
  });

  const selectedType =
    (registration?.selectedRegisterCompanyType as string) ||
    localStorage.getItem("selectedRegisterCompanyType");
  let previewNavCompanyType:
    | "standaloneCompany"
    | "subsidaryCompany"
    | null
    | undefined;
  if (!previewMode) {
    previewNavCompanyType = undefined;
  } else if (selectedType === "subsidaryCompany") {
    previewNavCompanyType = "subsidaryCompany";
  } else if (selectedType === "standaloneCompany") {
    previewNavCompanyType = "standaloneCompany";
  } else {
    previewNavCompanyType = null;
  }

  const handleBack = () => {
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — back is disabled.",
        autoClose: true,
      });
      return;
    }
    const alreadyRegisteredWith = localStorage.getItem("alreadyRegisteredWith");
    localStorage.removeItem("accountId");
    localStorage.removeItem("companyName");
    if (alreadyRegisteredWith) {
      dispatch(setRegisteredCompanies([], 0));
      window.location.href = getDXPRegistrationLink();
    } else {
      dispatch(setRegisteredCompanies(registeredCompanies, 1));
    }
  };

  const handleCreateOrSignIn = () => {
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — Adobe ID sign-in is disabled.",
        autoClose: true,
      });
      return;
    }
    const run = async () => {
      try {
        await ensureDxpAdobeImsBootstrapped();
      } catch {
        displayToasts({
          type: "error",
          text: "Adobe sign-in could not be loaded. Please refresh and try again.",
          autoClose: true,
        });
        return;
      }

      prepareDxpImsRedirectStorage();

      const ims = window.adobeIMS;
      if (!ims) {
        displayToasts({
          type: "error",
          text: "Adobe sign-in is not available. Please refresh and try again.",
          autoClose: true,
        });
        return;
      }

      try {
        let result: unknown;
        if (imsProfileExists) {
          result = ims.signIn();
        } else if (typeof ims.signUp === "function") {
          result = ims.signUp();
        } else {
          result = ims.signIn();
        }
        if (
          result != null &&
          typeof (result as Promise<unknown>).then === "function"
        ) {
          await (result as Promise<unknown>);
        }
      } catch {
        displayToasts({
          type: "error",
          text: "Could not open Adobe ID. Please try again.",
          autoClose: true,
        });
      }
    };

    run().catch(() => {
      /* unhandled rejection guard */
    });
  };

  return (
    <div
      className="registration-page-wrapper dxp-registration-page-shell"
      data-ims-ready={isIMSReady ? "true" : "false"}
    >
      <DxpRegistrationTopHeader program={PROGRAM} />
      <div className="registration-content-wrapper dxp-registration-content-row">
        <WizardNavLayout
          previewStepOverride={previewMode ? 2 : undefined}
          previewCompanyTypeOverride={previewNavCompanyType}
        />
        <View UNSAFE_className="dxp-wizard-main-column dxp-wizard-main-column--wide dxp-wizard-main-fill">
          <Flex direction="column" gap={0} alignItems="center">
            <View UNSAFE_className="dxp-wizard-step-panel dxp-wizard-step-panel-inner">
              <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                UNSAFE_className="dxp-wizard-flex-heading-row"
              >
                <Text UNSAFE_className="dxp-wizard-step-title-text">
                  {adobeIdConfig.title}
                </Text>
                <Text UNSAFE_className="dxp-wizard-step-meta-label">
                  {getStepLabel()}
                </Text>
              </Flex>
              <div className="dxp-wizard-divider-purple" aria-hidden />
              <AdobeIdMessageHtml html={message} />
              <div className="dxp-wizard-flex-spacer-grow" />

              <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                UNSAFE_className="dxp-wizard-footer-actions dxp-wizard-footer-actions--pair dxp-wizard-footer-mt"
              >
                <Button variant="secondary" size="M" onPress={handleBack}>
                  Back
                </Button>
                <Button
                  variant="accent"
                  size="M"
                  onPress={handleCreateOrSignIn}
                  UNSAFE_className="dxp-wizard-cta-inverse"
                >
                  {imsProfileExists
                    ? adobeIdConfig.signInLabel
                    : adobeIdConfig.createIdLabel}
                </Button>
              </Flex>
            </View>
          </Flex>
        </View>
      </div>
    </div>
  );
}

AdobeIdVerification.defaultProps = {
  previewMode: false,
};

export default AdobeIdVerification;
